import { useEffect, useState } from 'react'
import { api, ApiError } from '../api'
import {
  Badge,
  Empty,
  Field,
  FormGrid,
  Modal,
  Notice,
  Page,
  Panel,
  Table,
  fmtDateTime,
  fmtNumber,
  fmtPhone,
} from '../components/ui'
import { Logo } from '../components/Logo'
import type { AuditEntry, Did, DidPool, Disposition, DncPage, HealthDetail, Trunk } from '../types'

type Tab = 'trunks' | 'numbers' | 'dispositions' | 'dnc' | 'audit' | 'system'

export default function Settings() {
  const [tab, setTab] = useState<Tab>('trunks')
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)

  return (
    <Page title="Settings" subtitle="Telephony, outcomes, suppression and system">
      {notice && (
        <Notice kind={notice.kind} onDismiss={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      <div className="tabs">
        {(['trunks', 'numbers', 'dispositions', 'dnc', 'audit', 'system'] as Tab[]).map((t) => (
          <button key={t} className={tab === t ? 'tab on' : 'tab'} onClick={() => setTab(t)}>
            {t === 'dnc' ? 'DNC' : t}
          </button>
        ))}
      </div>

      {tab === 'trunks' && <Trunks notify={setNotice} />}
      {tab === 'numbers' && <Numbers notify={setNotice} />}
      {tab === 'dispositions' && <Dispositions notify={setNotice} />}
      {tab === 'dnc' && <Dnc notify={setNotice} />}
      {tab === 'audit' && <Audit />}
      {tab === 'system' && <System />}
    </Page>
  )
}

type Notify = (n: { kind: 'success' | 'error'; text: string } | null) => void

// =============================================================== trunks ====

function Trunks({ notify }: { notify: Notify }) {
  const [trunks, setTrunks] = useState<Trunk[]>([])
  const [adding, setAdding] = useState(false)
  const [config, setConfig] = useState<{ pjsip_config: string; note: string } | null>(null)

  const load = () => api.trunks().then(setTrunks).catch(() => setTrunks([]))
  useEffect(() => { load() }, [])

  const remove = async (t: Trunk) => {
    if (!confirm(`Delete trunk "${t.name}"?`)) return
    try {
      await api.deleteTrunk(t.id)
      load()
    } catch (e) {
      notify({ kind: 'error', text: e instanceof ApiError ? e.message : 'Could not delete' })
    }
  }

  return (
    <>
      <Panel
        title="Carrier trunks"
        actions={<button className="primary" onClick={() => setAdding(true)}>Add trunk</button>}
      >
        {trunks.length === 0 ? (
          <Empty
            message="No trunks configured."
            hint="A trunk is the SIP connection to your carrier. Without one, no outbound call can be placed."
          />
        ) : (
          <Table head={['Name', 'Host', 'Transport', 'Codecs', 'Max channels', 'Status', '']}>
            {trunks.map((t) => (
              <tr key={t.id}>
                <td className="strong">{t.name}</td>
                <td className="mono">{t.host}:{t.port}</td>
                <td>{t.transport}</td>
                <td className="mono small">{t.codecs}</td>
                <td>{t.max_channels}</td>
                <td><Badge tone={t.active ? 'ok' : 'muted'}>{t.active ? 'active' : 'disabled'}</Badge></td>
                <td className="row-actions">
                  <button className="link danger-text" onClick={() => remove(t)}>Delete</button>
                </td>
              </tr>
            ))}
          </Table>
        )}

        <Notice kind="warn">
          Adding a trunk here records it for campaign routing. It does not open a SIP port — that is
          deliberate. When you go live, restrict UDP 5060 to the carrier addresses rather than
          opening it to the internet, and add a fail2ban jail for Asterisk.
        </Notice>
      </Panel>

      {adding && (
        <TrunkEditor
          onClose={() => setAdding(false)}
          onSaved={(cfg) => {
            setAdding(false)
            load()
            setConfig(cfg)
          }}
        />
      )}

      {config && (
        <Modal title="Asterisk configuration" onClose={() => setConfig(null)} wide>
          <p className="muted small">{config.note}</p>
          <pre className="code-block">{config.pjsip_config}</pre>
          <div className="modal-actions">
            <button
              className="ghost"
              onClick={() => navigator.clipboard?.writeText(config.pjsip_config)}
            >
              Copy
            </button>
            <button className="primary" onClick={() => setConfig(null)}>Done</button>
          </div>
        </Modal>
      )}
    </>
  )
}

function TrunkEditor({
  onClose,
  onSaved,
}: {
  onClose: () => void
  onSaved: (cfg: { pjsip_config: string; note: string }) => void
}) {
  const [f, setF] = useState({
    name: '',
    host: '',
    port: 5060,
    transport: 'udp',
    auth_username: '',
    auth_password: '',
    from_domain: '',
    codecs: 'ulaw,alaw',
    max_channels: 100,
    active: true,
  })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    try {
      const r = await api.createTrunk({
        ...f,
        auth_username: f.auth_username || null,
        auth_password: f.auth_password || null,
        from_domain: f.from_domain || null,
      })
      onSaved({ pjsip_config: r.pjsip_config, note: r.note })
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title="Add carrier trunk" onClose={onClose} wide>
      {error && <Notice kind="error">{error}</Notice>}
      <FormGrid>
        <Field label="Name" hint="Used in campaign routing, e.g. carrier-a.">
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus />
        </Field>
        <Field label="Host" hint="The carrier SIP host.">
          <input value={f.host} onChange={(e) => setF({ ...f, host: e.target.value })} />
        </Field>
        <Field label="Port">
          <input type="number" value={f.port} onChange={(e) => setF({ ...f, port: +e.target.value })} />
        </Field>
        <Field label="Transport">
          <select value={f.transport} onChange={(e) => setF({ ...f, transport: e.target.value })}>
            <option value="udp">UDP</option>
            <option value="tcp">TCP</option>
            <option value="tls">TLS</option>
          </select>
        </Field>
        <Field label="Auth username" hint="Leave blank for IP authentication.">
          <input value={f.auth_username} onChange={(e) => setF({ ...f, auth_username: e.target.value })} />
        </Field>
        <Field label="Auth password">
          <input type="password" value={f.auth_password} onChange={(e) => setF({ ...f, auth_password: e.target.value })} />
        </Field>
        <Field
          label="Codecs"
          hint="G.711 only. Transcoding is what limits how many calls this box can carry."
        >
          <input value={f.codecs} onChange={(e) => setF({ ...f, codecs: e.target.value })} />
        </Field>
        <Field label="Max channels" hint="Cap so one trunk cannot consume the whole media server.">
          <input type="number" value={f.max_channels} onChange={(e) => setF({ ...f, max_channels: +e.target.value })} />
        </Field>
      </FormGrid>
      <div className="modal-actions">
        <button className="ghost" onClick={onClose}>Cancel</button>
        <button className="primary" onClick={save} disabled={busy || !f.name.trim() || !f.host.trim()}>
          {busy ? 'Saving…' : 'Add trunk'}
        </button>
      </div>
    </Modal>
  )
}

// ============================================================== numbers ====

function Numbers({ notify }: { notify: Notify }) {
  const [dids, setDids] = useState<Did[]>([])
  const [pools, setPools] = useState<DidPool[]>([])
  const [phone, setPhone] = useState('')
  const [poolId, setPoolId] = useState('')
  const [poolName, setPoolName] = useState('')

  const load = () => {
    api.dids().then(setDids).catch(() => setDids([]))
    api.didPools().then(setPools).catch(() => setPools([]))
  }
  useEffect(() => { load() }, [])

  const add = async () => {
    try {
      await api.createDid({ phone, pool_id: poolId || null })
      setPhone('')
      load()
    } catch (e) {
      notify({ kind: 'error', text: e instanceof ApiError ? e.message : 'Could not add' })
    }
  }

  const addPool = async () => {
    if (!poolName.trim()) return
    await api.createDidPool(poolName.trim(), 'sequential')
    setPoolName('')
    load()
  }

  return (
    <>
      <Panel title="Caller ID pools">
        <div className="inline-form">
          <input
            placeholder="New pool name"
            value={poolName}
            onChange={(e) => setPoolName(e.target.value)}
          />
          <button className="primary" onClick={addPool} disabled={!poolName.trim()}>Add pool</button>
        </div>
        {pools.length === 0 ? (
          <Empty message="No pools." hint="A pool groups the numbers a campaign presents." />
        ) : (
          <Table head={['Pool', 'Strategy', 'Numbers']}>
            {pools.map((p) => (
              <tr key={p.id}>
                <td className="strong">{p.name}</td>
                <td>{p.strategy}</td>
                <td>{p.numbers}</td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      <Panel title="Numbers">
        <div className="inline-form">
          <input placeholder="Number to add" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <select value={poolId} onChange={(e) => setPoolId(e.target.value)}>
            <option value="">No pool</option>
            {pools.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button className="primary" onClick={add} disabled={!phone.trim()}>Add number</button>
        </div>

        {dids.length === 0 ? (
          <Empty
            message="No numbers registered."
            hint="These are the numbers shown to the people you call."
          />
        ) : (
          <Table head={['Number', 'Pool', 'CNAM', 'Attestation', 'Last used', 'Status', '']}>
            {dids.map((d) => (
              <tr key={d.id} className={d.spam_flagged_at ? 'row-danger' : ''}>
                <td className="mono strong">{fmtPhone(d.phone_e164)}</td>
                <td>{d.pool ?? <span className="muted">—</span>}</td>
                <td>{d.cnam ?? '—'}</td>
                <td>
                  {d.attestation ? (
                    <Badge tone={d.attestation === 'A' ? 'ok' : 'warn'}>{d.attestation}</Badge>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td className="muted">{fmtDateTime(d.last_used_at)}</td>
                <td>
                  {d.spam_flagged_at ? (
                    <Badge tone="danger">spam flagged</Badge>
                  ) : (
                    <Badge tone={d.active ? 'ok' : 'muted'}>{d.active ? 'active' : 'retired'}</Badge>
                  )}
                </td>
                <td className="row-actions">
                  {!d.spam_flagged_at && (
                    <button
                      className="link danger-text"
                      onClick={async () => { await api.flagDid(d.id); load() }}
                      title="Retire this number from rotation"
                    >
                      Flag spam
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}

        <p className="muted small">
          A number carriers have flagged drags down the answer rate of every call placed with it.
          Flagging retires it from rotation immediately.
        </p>
      </Panel>
    </>
  )
}

// ========================================================= dispositions ====

function Dispositions({ notify }: { notify: Notify }) {
  const [rows, setRows] = useState<Disposition[]>([])
  const [adding, setAdding] = useState(false)
  const [f, setF] = useState({
    code: '', label: '', is_contact: false, is_sale: false,
    is_dnc: false, is_callback: false, is_final: false, sort_order: 100,
  })

  const load = () => api.dispositions().then(setRows).catch(() => setRows([]))
  useEffect(() => { load() }, [])

  const save = async () => {
    try {
      await api.createDisposition({ ...f, campaign_id: null })
      setAdding(false)
      setF({ code: '', label: '', is_contact: false, is_sale: false, is_dnc: false, is_callback: false, is_final: false, sort_order: 100 })
      load()
    } catch (e) {
      notify({ kind: 'error', text: e instanceof ApiError ? e.message : 'Could not save' })
    }
  }

  return (
    <>
      <Panel
        title="Dispositions"
        actions={<button className="primary" onClick={() => setAdding(true)}>Add</button>}
      >
        <Table head={['Code', 'Label', 'Contact', 'Sale', 'DNC', 'Callback', 'Final', '']}>
          {rows.map((d) => (
            <tr key={d.id}>
              <td className="mono strong">{d.code}</td>
              <td>{d.label}</td>
              <td>{d.is_contact ? '✓' : ''}</td>
              <td>{d.is_sale ? '✓' : ''}</td>
              <td>{d.is_dnc ? <Badge tone="danger">DNC</Badge> : ''}</td>
              <td>{d.is_callback ? '✓' : ''}</td>
              <td>{d.is_final ? '✓' : ''}</td>
              <td className="row-actions">
                <button
                  className="link danger-text"
                  onClick={async () => { await api.deleteDisposition(d.id); load() }}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </Table>
        <p className="muted small">
          The flags are what the lead engine branches on, not the code. Marking one <b>DNC</b> adds
          the number to the internal suppression list whenever an agent selects it.
        </p>
      </Panel>

      {adding && (
        <Modal title="Add disposition" onClose={() => setAdding(false)}>
          <FormGrid>
            <Field label="Code" hint="Short, uppercase.">
              <input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} autoFocus />
            </Field>
            <Field label="Label">
              <input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
            </Field>
          </FormGrid>
          <div className="check-row wrap">
            {([
              ['is_contact', 'Counts as a contact'],
              ['is_sale', 'Counts as a sale'],
              ['is_dnc', 'Adds to do-not-call'],
              ['is_callback', 'Schedules a callback'],
              ['is_final', 'Retires the lead'],
            ] as const).map(([k, label]) => (
              <label key={k}>
                <input
                  type="checkbox"
                  checked={f[k]}
                  onChange={(e) => setF({ ...f, [k]: e.target.checked })}
                />
                {label}
              </label>
            ))}
          </div>
          <div className="modal-actions">
            <button className="ghost" onClick={() => setAdding(false)}>Cancel</button>
            <button className="primary" onClick={save} disabled={!f.code.trim() || !f.label.trim()}>
              Add
            </button>
          </div>
        </Modal>
      )}
    </>
  )
}

// ================================================================== DNC ====

function Dnc({ notify }: { notify: Notify }) {
  const [data, setData] = useState<DncPage | null>(null)
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)
  const [adding, setAdding] = useState(false)
  const [numbers, setNumbers] = useState('')
  const [reason, setReason] = useState('')

  const load = () => api.dnc({ q: q || undefined, page }).then(setData).catch(() => setData(null))
  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, page])

  const add = async () => {
    try {
      const r = await api.addDnc(numbers, 'internal', reason || undefined)
      notify({
        kind: 'success',
        text: `Added ${r.added} numbers.${r.rejected.length ? ` ${r.rejected.length} could not be parsed.` : ''}`,
      })
      setAdding(false)
      setNumbers('')
      setReason('')
      load()
    } catch (e) {
      notify({ kind: 'error', text: e instanceof ApiError ? e.message : 'Could not add' })
    }
  }

  return (
    <>
      <Panel
        title="Do not call"
        actions={<button className="primary" onClick={() => setAdding(true)}>Add numbers</button>}
      >
        <div className="filter-bar">
          <input
            className="grow"
            placeholder="Search suppressed numbers"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0) }}
          />
          <span className="muted">{data ? `${fmtNumber(data.total)} suppressed` : ''}</span>
        </div>

        {!data || data.entries.length === 0 ? (
          <Empty
            message="No suppressed numbers."
            hint="Every dial attempt is checked against this list before it is placed."
          />
        ) : (
          <>
            <Table head={['Number', 'Scope', 'Source', 'Reason', 'Added', '']}>
              {data.entries.map((e) => (
                <tr key={e.id}>
                  <td className="mono strong">{fmtPhone(e.phone_e164)}</td>
                  <td><Badge tone={e.scope === 'federal' ? 'danger' : 'muted'}>{e.scope}</Badge></td>
                  <td className="muted">{e.source ?? '—'}</td>
                  <td className="muted">{e.reason ?? '—'}</td>
                  <td className="muted">{fmtDateTime(e.added_at)}</td>
                  <td className="row-actions">
                    <button
                      className="link danger-text"
                      onClick={async () => {
                        if (!confirm('Remove this suppression? The number becomes dialable again.')) return
                        await api.removeDnc(e.id)
                        load()
                      }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </Table>
            <div className="pager">
              <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Previous</button>
              <span className="muted">Page {page + 1}</span>
              <button
                disabled={data.entries.length < 50}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          </>
        )}
      </Panel>

      {adding && (
        <Modal title="Add to do-not-call" onClose={() => setAdding(false)}>
          <Field
            label="Numbers"
            hint="One per line, or comma separated. Any format — they are normalised."
          >
            <textarea rows={8} value={numbers} onChange={(e) => setNumbers(e.target.value)} />
          </Field>
          <Field label="Reason" hint="Recorded in the audit trail.">
            <input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <div className="modal-actions">
            <button className="ghost" onClick={() => setAdding(false)}>Cancel</button>
            <button className="primary" onClick={add} disabled={!numbers.trim()}>Add</button>
          </div>
        </Modal>
      )}
    </>
  )
}

// ================================================================ audit ====

function Audit() {
  const [rows, setRows] = useState<AuditEntry[]>([])
  const [page, setPage] = useState(0)

  useEffect(() => {
    api.audit({ page }).then(setRows).catch(() => setRows([]))
  }, [page])

  return (
    <Panel title="Audit trail" actions={<span className="muted small">who changed what</span>}>
      {rows.length === 0 ? (
        <Empty message="Nothing recorded yet." />
      ) : (
        <>
          <Table head={['When', 'Who', 'Action', 'Entity', 'Detail']}>
            {rows.map((a) => (
              <tr key={a.id}>
                <td className="muted">{fmtDateTime(a.at)}</td>
                <td>{a.actor ?? <span className="muted">system</span>}</td>
                <td className="mono">{a.action}</td>
                <td className="muted">{a.entity_type ?? '—'}</td>
                <td className="mono small muted">
                  {a.detail ? JSON.stringify(a.detail).slice(0, 90) : ''}
                </td>
              </tr>
            ))}
          </Table>
          <div className="pager">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Previous</button>
            <span className="muted">Page {page + 1}</span>
            <button disabled={rows.length < 50} onClick={() => setPage((p) => p + 1)}>Next →</button>
          </div>
        </>
      )}
    </Panel>
  )
}

// =============================================================== system ====

function System() {
  // Admin-only screen, so it reads the measured endpoint rather than the thin
  // liveness one: an admin looking at system state wants the numbers.
  const [health, setHealth] = useState<HealthDetail | null>(null)
  const [chain, setChain] = useState<{ intact: boolean } | null>(null)

  useEffect(() => {
    api.healthDetail().then(setHealth).catch(() => setHealth(null))
    api.compliance().then((c) => setChain({ intact: c.chain_intact })).catch(() => setChain(null))
  }, [])

  return (
    <Panel title="System">
      <div className="maker">
        <Logo size={34} />
        <div>
          <div className="maker-name">Impact Dialer</div>
          <div className="muted small">
            Built by{' '}
            <a href="https://pinkviperlabs.com" target="_blank" rel="noreferrer noopener">
              PinkViper Labs
            </a>
          </div>
        </div>
      </div>

      <dl className="kv wide">
        <div><dt>Version</dt><dd className="mono">{health?.version ?? '—'}</dd></div>
        <div>
          <dt>Database</dt>
          <dd>
            <Badge tone={health?.database.connected ? 'ok' : 'danger'}>
              {health
                ? `${health.database.connected ? 'connected' : 'unreachable'} · ${health.database.latency_ms} ms`
                : '—'}
            </Badge>
          </dd>
        </div>
        <div>
          <dt>Media server</dt>
          <dd>
            {health?.media.asterisk_version ? (
              <span className="mono small">Asterisk {health.media.asterisk_version}</span>
            ) : (
              <Badge tone="danger">unreachable</Badge>
            )}
          </dd>
        </div>
        <div>
          <dt>Compliance log</dt>
          <dd>
            {chain === null ? '—' : (
              <Badge tone={chain.intact ? 'ok' : 'danger'}>
                {chain.intact ? 'chain verified intact' : 'chain broken'}
              </Badge>
            )}
          </dd>
        </div>
      </dl>

      <p className="muted small">
        The compliance chain is verified by recomputing every hash in the decision log. It can be
        checked independently from a read-only database session with{' '}
        <code>SELECT * FROM verify_compliance_chain();</code> — no rows returned means intact.
      </p>
    </Panel>
  )
}
